import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HeaderOne from './HeaderOne';
import FooterOne from './BottomFooter';

const ComparisonPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const initialMaterialId = searchParams.get('material_id');
    const productTypeId = searchParams.get('product_type_id');
    const customizationDataStr = searchParams.get('customizations');
    const initialWidth = searchParams.get('width') || 36;
    const initialHeight = searchParams.get('height') || 24;

    const [comparisonData, setComparisonData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [leftMaterial, setLeftMaterial] = useState(null);
    const [rightMaterial, setRightMaterial] = useState(null);
    const [width, setWidth] = useState(initialWidth);
    const [height, setHeight] = useState(initialHeight);
    const [leftPrice, setLeftPrice] = useState(0);
    const [rightPrice, setRightPrice] = useState(0);

    const [customizations, setCustomizations] = useState({});
    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return 'assets/images/thumbs/default-material.png';
        if (imageUrl.startsWith('http')) return imageUrl;
        const IMG_URL = process.env.REACT_APP_IMG_BASE_URL;
        return `${IMG_URL}${imageUrl}`;
    };

    useEffect(() => {
        const fetchComparison = async () => {
            if (!initialMaterialId || !productTypeId) {
                setError("Missing comparison parameters");
                setIsLoading(false);
                return;
            }

            try {
                const API_URL = `${process.env.REACT_APP_API_URL}`;
                const customizationData = customizationDataStr ? JSON.parse(decodeURIComponent(customizationDataStr)) : {};

                const response = await axios.post(
                    `${API_URL}/comparisons/compare_materials/`,
                    {
                        product_type_id: parseInt(productTypeId),
                        material_id: parseInt(initialMaterialId),
                        width_inches: parseFloat(initialWidth),
                        height_inches: parseFloat(initialHeight),
                        customization_data: customizationData
                    },
                    { headers: { 'Content-Type': 'application/json' } }
                );

                setComparisonData(response.data);
                setLeftMaterial(response.data.selected_material);

                const initialCustom = {};
                response.data.customizations.forEach((custom, idx) => {
                    initialCustom[idx] = {
                        option_id: custom.selected_option_id,
                        value: custom.selected_value,
                        price_modifier_flat: custom.price_modifier_flat,
                        price_modifier_percent: custom.price_modifier_percent
                    };
                });
                setCustomizations(initialCustom);


                const leftPriceVal = calculatePrice(response.data.selected_material, parseFloat(initialWidth), parseFloat(initialHeight), initialCustom);
                setLeftPrice(leftPriceVal);

                setError(null);
                setIsLoading(false);

            } catch (error) {
                console.error("Error fetching comparison:", error);
                setError(error.response?.data?.error || error.message || "Failed to load comparison");
                setIsLoading(false);
            }
        };

        fetchComparison();
    }, [initialMaterialId, productTypeId, customizationDataStr, initialWidth, initialHeight]);

    const calculatePrice = (material, w, h, customizationMap) => {
        if (!material) return 0;

        const area_sqft = (parseFloat(w) * parseFloat(h)) / 144.0;
        let price = parseFloat(material.cost_per_sqft) * area_sqft;

        Object.values(customizationMap).forEach(custom => {
            if (custom.price_modifier_flat) {
                price += parseFloat(custom.price_modifier_flat);
            }
            if (custom.price_modifier_percent) {
                price += (price * parseFloat(custom.price_modifier_percent) / 100);
            }
        });

        return Math.round(price * 100) / 100;
    };

    const handleCustomizationChange = (fieldIdx, newOption) => {
        const updated = {
            ...customizations,
            [fieldIdx]: newOption
        };
        setCustomizations(updated);

        if (leftMaterial) {
            const newLeftPrice = calculatePrice(leftMaterial, width, height, updated);
            setLeftPrice(newLeftPrice);
        }
        if (rightMaterial) {
            const newRightPrice = calculatePrice(rightMaterial, width, height, updated);
            setRightPrice(newRightPrice);
        }
    };

    const handleDimensionChange = (type, value) => {
        const newValue = parseFloat(value) || 0;
        if (newValue <= 0) return;

        if (type === 'width') setWidth(newValue);
        if (type === 'height') setHeight(newValue);

        const newW = type === 'width' ? newValue : width;
        const newH = type === 'height' ? newValue : height;

        if (leftMaterial) {
            const newLeftPrice = calculatePrice(leftMaterial, newW, newH, customizations);
            setLeftPrice(newLeftPrice);
        }
        if (rightMaterial) {
            const newRightPrice = calculatePrice(rightMaterial, newW, newH, customizations);
            setRightPrice(newRightPrice);
        }
    };

    const handleSelectMaterial = (material) => {
        if (rightMaterial?.id === material.id) {
            setRightMaterial(null);
        } else {
            setRightMaterial(material);
            const newPrice = calculatePrice(material, width, height, customizations);
            setRightPrice(newPrice);
        }
    };

    const handleDropdownSelect = (materialId) => {
        const material = comparisonData.similar_materials.find(m => m.id === parseInt(materialId));
        if (material) {
            handleSelectMaterial(material);
        }
    };

    const areaSize = comparisonData ? Math.round((width * height / 144) * 100) / 100 : 0;

    return (
        <>
            <HeaderOne />

            <div className="comparison-page">
                {isLoading && (
                    <div className="container">
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading comparison...</p>
                        </div>
                    </div>
                )}

                {error && !isLoading && (
                    <div className="container py-80">
                        <div className="error-state">
                            <i className="ph ph-warning"></i>
                            <h3>Error</h3>
                            <p>{error}</p>
                            <Link to="/" className="btn btn-primary">Go Back</Link>
                        </div>
                    </div>
                )}

                {!isLoading && !error && comparisonData && (
                    <section className="py-80">
                        <div className="container">
                            <div className="comparison-header">
                                <h2><i className="ph ph-shuffle"></i> Compare Materials</h2>
                                <Link to="/" className="btn btn-primary">
                                    <i className="ph ph-arrow-left"></i> Back
                                </Link>
                            </div>

                            {/* ===== MAIN WRAPPER ===== */}
                            <div className="main-wrapper">
                                {/* LEFT SIDE: Materials + Specifications */}
                                <div className="left-section">
                                    {/* SELECTED MATERIALS */}
                                    <div className="materials-compact-section">
                                        <div className="compact-materials-display">
                                            {/* LEFT MATERIAL */}
                                            <div className="material-compact-card active">
                                                {leftMaterial && (
                                                    <>
                                                        {leftMaterial.image_swatch && (
                                                            <img src={getImageUrl(leftMaterial.image_swatch)} alt={leftMaterial.name} />
                                                        )}
                                                        <h5>{leftMaterial.name}</h5>
                                                        <p className="compact-cost">${leftMaterial.cost_per_sqft}/sq ft</p>
                                                        <div className="compact-price">${leftPrice}</div>
                                                    </>
                                                )}
                                            </div>

                                            {/* VS */}
                                            <div className="compact-vs">VS</div>

                                            {/* RIGHT MATERIAL */}
                                            <div className="material-compact-card">
                                                {rightMaterial ? (
                                                    <>
                                                        {rightMaterial.image_swatch && (
                                                            <img src={getImageUrl(rightMaterial.image_swatch)} alt={rightMaterial.name} />
                                                        )}
                                                        <h5>{rightMaterial.name}</h5>
                                                        <p className="compact-cost">${rightMaterial.cost_per_sqft}/sq ft</p>
                                                        <div className="compact-price">${rightPrice}</div>
                                                        {leftPrice > 0 && (
                                                            <div className={`compact-diff ${rightPrice < leftPrice ? 'save' : 'extra'}`}>
                                                                {rightPrice < leftPrice ? '✓' : '+'} ${Math.abs(rightPrice - leftPrice).toFixed(2)}
                                                            </div>
                                                        )}
                                                        <button
                                                            className="btn-change-compact"
                                                            onClick={() => setRightMaterial(null)}
                                                        >
                                                            Change
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="material-select-dropdown">
                                                        <label>Select Material</label>
                                                        <select
                                                            className="material-dropdown-inline"
                                                            onChange={(e) => handleDropdownSelect(e.target.value)}
                                                            defaultValue=""
                                                        >
                                                            <option value="" disabled>Choose Material</option>
                                                            {comparisonData.similar_materials && comparisonData.similar_materials.map((material) => (
                                                                <option key={material.id} value={material.id}>
                                                                    {material.name} - ${material.cost_per_sqft}/sq ft
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* SPECIFICATIONS - BELOW MATERIALS */}
                                    <div className="specifications-section">
                                        <div className="spec-title">Specifications</div>
                                        <div className="spec-controls">
                                            <div className="spec-input">
                                                <label>Width</label>
                                                <input
                                                    type="number"
                                                    value={width}
                                                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="spec-input">
                                                <label>Height</label>
                                                <input
                                                    type="number"
                                                    value={height}
                                                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="spec-area">
                                                <label>Area</label>
                                                <div className="spec-area-value">{areaSize} sq ft</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT SIDE: CUSTOMIZATIONS */}
                                {comparisonData.customizations && comparisonData.customizations.length > 0 && (
                                    <div className="customizations-section">
                                        <div className="custom-title">Customizations</div>

                                        {/* CUSTOMIZATION DROPDOWNS */}
                                        <div className="custom-fields">
                                            {comparisonData.customizations.map((custom, idx) => (
                                                <div key={idx} className="custom-field-item">
                                                    <label>{custom.field_name}</label>
                                                    <select
                                                        value={customizations[idx]?.option_id || custom.selected_option_id}
                                                        onChange={(e) => {
                                                            const selectedOpt = custom.options.find(
                                                                opt => opt.id === parseInt(e.target.value)
                                                            );
                                                            handleCustomizationChange(idx, {
                                                                option_id: selectedOpt.id,
                                                                value: selectedOpt.value,
                                                                price_modifier_flat: selectedOpt.price_modifier_flat,
                                                                price_modifier_percent: selectedOpt.price_modifier_percent
                                                            });
                                                        }}
                                                        className="custom-select"
                                                    >
                                                        {custom.options.map((option) => (
                                                            <option key={option.id} value={option.id}>
                                                                {option.value}
                                                                {option.price_modifier_flat > 0 && ` (+$${option.price_modifier_flat})`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ===== SIMILAR MATERIALS ===== */}
                            {comparisonData.similar_materials && comparisonData.similar_materials.length > 0 && (
                                <>
                                    <div className="section-title">
                                        <h3>Available Materials to Compare</h3>
                                    </div>

                                    <div className="similar-materials-section">
                                        <div className="materials-grid">
                                            {comparisonData.similar_materials.map((material) => (
                                                <div
                                                    key={material.id}
                                                    className={`material-grid-card ${rightMaterial?.id === material.id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectMaterial(material)}
                                                >
                                                    {material.image_swatch && (
                                                        <img src={getImageUrl(material.image_swatch)} alt={material.name} />
                                                    )}
                                                    <h5>{material.name}</h5>
                                                    <p className="grid-cost">${material.cost_per_sqft}/sq ft</p>
                                                    <div className="grid-select-btn">
                                                        {rightMaterial?.id === material.id ? '✓ Selected' : 'Select'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}
            </div>

            <style jsx>{`
                * {
                    box-sizing: border-box;
                }

                html, body {
                    width: 100%;
                    overflow-x: hidden;
                    background: #0F1419;
                }

                .comparison-page {
                    background: #fbfbfb;
                    min-height: 100vh;
                    padding: 0;
                    width: 100%;
                    overflow-x: hidden;
                    color: #1a1a1a;
                    font-family: 'Inter', sans-serif;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 15px;
                    width: 100%;
                }

                .py-80 {
                    padding: 80px 0;
                }

                /* ===== HEADER ===== */
                .comparison-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 40px;
                    flex-wrap: wrap;
                    gap: 15px;
                    width: 100%;
                }

                .comparison-header h2 {
                    margin: 0;
                    font-size: 32px;
                    font-weight: 800;
                    color: #1a1a1a;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .comparison-header h2 i {
                    color: #93B0AC;
                }

                .btn-primary {
                    padding: 12px 24px !important;
                    background: #C3DCD8 !important;
                    color: #93B0AC !important;
                    border: none !important;
                    border-radius: 12px !important;
                    font-weight: 800 !important;
                    text-decoration: none;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 15px rgba(195, 220, 216, 0.4);
                }

                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(195, 220, 216, 0.5);
                    background: #b5d2cd !important;
                }

                /* ===== MAIN WRAPPER ===== */
                .main-wrapper {
                    display: grid;
                    grid-template-columns: 1.2fr 0.8fr;
                    gap: 30px;
                    margin-bottom: 50px;
                }

                .left-section {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                /* ===== MATERIALS SECTION ===== */
                .materials-compact-section {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 30px;
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                }

                .compact-materials-display {
                    display: grid;
                    grid-template-columns: 1fr auto 1fr;
                    gap: 20px;
                    align-items: center;
                }

                .material-compact-card {
                    background: #f8fafc;
                    border: 1.5px solid #f1f5f9;
                    border-radius: 16px;
                    padding: 20px;
                    text-align: center;
                    transition: all 0.3s ease;
                    min-height: 220px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .material-compact-card.active {
                    border-color: #C3DCD8;
                    background: #eff6f5;
                    box-shadow: 0 8px 25px rgba(195, 220, 216, 0.3);
                }

                .material-compact-card img {
                    width: 100%;
                    height: 120px;
                    object-fit: contain;
                    border-radius: 12px;
                    margin-bottom: 15px;
                    background: #fff;
                    padding: 10px;
                    border: 1px solid rgba(0,0,0,0.03);
                }

                .material-compact-card h5 {
                    color: #1a1a1a;
                    font-size: 16px;
                    font-weight: 700;
                    margin-bottom: 8px;
                }

                .compact-cost {
                    color: #64748b;
                    font-size: 13px;
                    font-weight: 500;
                }

                .compact-price {
                    font-size: 24px;
                    font-weight: 800;
                    color: #93B0AC;
                    margin-top: auto;
                }

                .compact-vs {
                    color: #cbd5e1;
                    font-weight: 900;
                    font-size: 18px;
                    padding: 0 10px;
                }

                .material-select-dropdown {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                    padding: 20px;
                    justify-content: center;
                    min-height: 220px;
                }

                .material-dropdown-inline {
                    background: #ffffff;
                    border: 1.5px solid #f1f5f9;
                    border-radius: 12px;
                    color: #1a1a1a;
                    padding: 12px;
                    outline: none;
                    transition: all 0.3s;
                    font-weight: 600;
                }

                .material-dropdown-inline:focus {
                    border-color: #C3DCD8;
                    background: #fff;
                    box-shadow: 0 8px 25px rgba(195, 220, 216, 0.4);
                }

                .btn-change-compact {
                    margin-top: 15px;
                    background: #f1f5f9;
                    color: #64748b;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 8px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.3s;
                    font-weight: 700;
                }

                .btn-change-compact:hover {
                    background: #1a1a1a;
                    color: #ffffff;
                }

                .compact-diff {
                    font-size: 11px;
                    font-weight: 800;
                    padding: 4px 10px;
                    border-radius: 6px;
                    margin-top: 8px;
                    text-transform: uppercase;
                }

                .compact-diff.save {
                    background: #eff6f5;
                    color: #93B0AC;
                    border: 1px solid rgba(147, 176, 172, 0.2);
                }

                .compact-diff.extra {
                    background: #fdf2f2;
                    color: #ef4444;
                    border: 1px solid rgba(239, 68, 68, 0.1);
                }

                /* ===== SPECIFICATIONS ===== */
                .specifications-section {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 25px;
                    border: 1px solid rgba(0,0,0,0.05);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                }
                }

                .spec-title, .custom-title {
                    font-size: 18px;
                    font-weight: 800;
                    color: #1a1a1a;
                    margin-bottom: 20px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .spec-title::before, .custom-title::before {
                    content: '';
                    width: 4px;
                    height: 18px;
                    background: #C3DCD8;
                    border-radius: 2px;
                }

                .spec-controls {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                }

                .spec-input label, .custom-field-item label {
                    color: #64748b;
                    font-size: 12px;
                    font-weight: 700;
                    margin-bottom: 8px;
                    display: block;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .spec-input input, .custom-select {
                    background: #f8fafc;
                    border: 2px solid #f1f5f9;
                    border-radius: 12px;
                    color: #1a1a1a;
                    padding: 12px;
                    width: 100%;
                    outline: none;
                    transition: all 0.3s;
                    font-weight: 700;
                }

                .spec-input input:focus, .custom-select:focus {
                    border-color: #C3DCD8;
                    background: #fff;
                    box-shadow: 0 8px 25px rgba(195, 220, 216, 0.4);
                }

                .spec-area-value {
                    background: #eff6f5;
                    color: #93B0AC;
                    padding: 12px;
                    border-radius: 12px;
                    font-weight: 800;
                    border: 1px solid rgba(147, 176, 172, 0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* ===== CUSTOMIZATIONS SECTION ===== */
                .customizations-section {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 30px;
                    border: 1px solid rgba(0,0,0,0.05);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                    height: fit-content;
                    position: sticky;
                    top: 100px;
                }

                .custom-fields {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                /* ===== SIMILAR MATERIALS ===== */
                .section-title {
                    margin: 60px 0 30px;
                    border-bottom: 2px solid #f1f5f9;
                    padding-bottom: 15px;
                }

                .section-title h3 {
                    color: #1a1a1a;
                    font-size: 24px;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .materials-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
                    gap: 24px;
                }

                .material-grid-card {
                    background: #ffffff;
                    border: 1.5px solid #f1f5f9;
                    border-radius: 20px;
                    padding: 20px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.02);
                }

                .material-grid-card:hover {
                    transform: translateY(-8px);
                    border-color: #C3DCD8;
                    box-shadow: 0 15px 35px rgba(0,0,0,0.08);
                }

                .material-grid-card.selected {
                    background: #eff6f5;
                    border-color: #C3DCD8;
                    box-shadow: 0 15px 35px rgba(195, 220, 216, 0.3);
                }

                .material-grid-card img {
                    width: 100%;
                    height: 140px;
                    object-fit: contain;
                    border-radius: 12px;
                    margin-bottom: 15px;
                    background: #f8fafc;
                    padding: 10px;
                    border: 1px solid rgba(0,0,0,0.03);
                }

                .material-grid-card h5 {
                    color: #1a1a1a;
                    font-size: 16px;
                    font-weight: 700;
                    margin: 0 0 10px 0;
                }

                .grid-cost {
                    color: #64748b;
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 20px;
                }

                .grid-select-btn {
                    margin-top: auto;
                    padding: 12px;
                    border-radius: 12px;
                    background: #f1f5f9;
                    color: #64748b;
                    text-align: center;
                    font-weight: 800;
                    transition: all 0.3s;
                    text-transform: uppercase;
                    font-size: 12px;
                    letter-spacing: 0.5px;
                }

                .material-grid-card:hover .grid-select-btn {
                    background: #1a1a1a;
                    color: #ffffff;
                }

                .material-grid-card.selected .grid-select-btn {
                    background: #C3DCD8;
                    color: #93B0AC;
                }

                /* ===== LOADING & ERROR ===== */
                .loading-state, .error-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                    gap: 20px;
                }

                .spinner {
                    width: 50px;
                    height: 50px;
                    border: 4px solid #f1f5f9;
                    border-top: 4px solid #C3DCD8;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .error-state i {
                    font-size: 64px;
                    color: #ef4444;
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 1024px) {
                    .main-wrapper {
                        grid-template-columns: 1fr;
                    }
                    .customizations-section {
                        position: static;
                    }
                }

                @media (max-width: 768px) {
                    .py-80 { padding: 40px 0; }
                    .spec-controls { grid-template-columns: 1fr; }
                    .compact-materials-display { grid-template-columns: 1fr; text-align: center; }
                    .compact-vs { padding: 15px 0; transform: rotate(90deg); margin: 10px 0; }
                    .comparison-header h2 { font-size: 24px; }
                }
            `}</style>

            <FooterOne />
        </>
    );
};

export default ComparisonPage;
